import {
  DisplayRightsProposalStatus,
  MentionBoostAuthorizationStatus,
  type DisplayRightsLaunchBlockedData,
  type DisplayRightsProposalSummary,
} from '@fluenceur/types';

/**
 * The two sides a launch is validated against, as the panel shows them.
 *
 * A mirror of the server rule, never a second version of it: the server refuses
 * the launch either way. What this buys is a marketer who sees WHICH side is
 * missing before clicking, instead of discovering it in an error toast.
 */
export interface DisplayRightsLaunchGate {
  /**
   * Whether compensation was declared at all. False means no checklist, no
   * checkbox, nothing: an authorization nobody attached terms to launches
   * exactly as it did before this gate existed.
   */
  applies: boolean;
  /** Instagram granted the permission. */
  metaSatisfied: boolean;
  /** A live agreement covers the launch, or an off-platform one was affirmed. */
  conditionsSatisfied: boolean;
  /**
   * Whether the version in force could carry an affirmation at all. Mirrors the
   * server rule: a draft was never offered to the creator and a replaced version
   * names terms that no longer stand, so neither can be affirmed about — and an
   * escape hatch the server would refuse must never be offered.
   */
  attestable: boolean;
}

/**
 * Whether the agreement still covers a launch: accepted, not terminated, and
 * either not started yet or still inside its run time.
 *
 * Mirrors the model rule exactly, including the "accepted but never launched"
 * case — the run time starts at the first ad, so an agreement with no end date
 * is a fresh agreement, not an expired one.
 */
function hasLiveAgreement(summary: DisplayRightsProposalSummary, now: Date): boolean {
  if (summary.status !== DisplayRightsProposalStatus.ACCEPTED) return false;
  if (summary.rightsEndedAt) return false;
  if (!summary.rightsEndAt) return true;

  const endsAt = new Date(summary.rightsEndAt).getTime();

  return Number.isFinite(endsAt) && endsAt > now.getTime();
}

/**
 * Whether terms ever reached the creator, which is what makes compensation
 * DECLARED. A draft alone does not: composition writes it and delivers it in two
 * steps, so a failure in between leaves a draft nobody was ever shown.
 *
 * A version numbered above the first implies an earlier one WAS sent — only a
 * sent version can be replaced — so a chain caught mid-revision still counts,
 * the same way the server counts it by looking at the whole chain.
 */
function hasReachedCreator(summary: DisplayRightsProposalSummary): boolean {
  return summary.sentAt !== null || summary.versionNumber > 1;
}

/** Where the double validation stands for one authorization. */
export function resolveDisplayRightsLaunchGate(
  summary: DisplayRightsProposalSummary | null,
  authorizationStatus: MentionBoostAuthorizationStatus | null | undefined,
  now: Date = new Date()
): DisplayRightsLaunchGate {
  const metaSatisfied = authorizationStatus === MentionBoostAuthorizationStatus.AUTHORIZED;

  if (!summary || !hasReachedCreator(summary)) {
    return { applies: false, metaSatisfied, conditionsSatisfied: true, attestable: false };
  }

  return {
    applies: true,
    metaSatisfied,
    conditionsSatisfied: hasLiveAgreement(summary, now) || summary.offPlatformAttestedAt !== null,
    attestable:
      summary.status !== DisplayRightsProposalStatus.DRAFT &&
      summary.status !== DisplayRightsProposalStatus.SUPERSEDED,
  };
}

/**
 * Whether the launch button may fire, given what the marketer ticked.
 *
 * The escape hatch covers the compensation only. It can never stand in for a
 * permission Instagram has not granted — there would be nothing to run the ad
 * with, whatever anyone agreed off platform.
 */
export function isDisplayRightsLaunchAllowed(
  gate: DisplayRightsLaunchGate,
  offPlatformAttestation: boolean
): boolean {
  if (!gate.applies) return true;

  return (
    gate.metaSatisfied && (gate.conditionsSatisfied || (offPlatformAttestation && gate.attestable))
  );
}

/**
 * Whether ticking the escape hatch would actually unblock this launch — the one
 * case where offering it is honest. Offering it while Instagram is still the
 * blocker would promise the marketer a way through that does not exist.
 */
export function canAttestOffPlatform(gate: DisplayRightsLaunchGate): boolean {
  return gate.applies && gate.metaSatisfied && !gate.conditionsSatisfied && gate.attestable;
}

/**
 * What the server actually refused, as sentences to show the marketer.
 *
 * The panel computes its own reading before the click; this reads the server's,
 * after. They differ exactly when the world moved in between — the creator
 * answered, a teammate revised, Instagram withdrew — which is the moment a
 * generic "something went wrong" is least useful.
 *
 * Returns translation keys and their parameters rather than strings, so the
 * caller stays free of i18n plumbing and the copy stays testable.
 */
export function displayRightsLaunchBlockedLines(
  data: DisplayRightsLaunchBlockedData
): Array<{ key: string; params?: Record<string, unknown> }> {
  const lines: Array<{ key: string; params?: Record<string, unknown> }> = [];

  if (data.missing.includes('meta_authorization')) {
    lines.push({ key: 'mentions.boost.launch.gate.blocked.meta' });
  }

  if (data.missing.includes('display_rights_conditions')) {
    // A draft is its own sentence: "awaiting acceptance" would be a lie about
    // terms that were never sent, and the remedy is to send them, not to wait.
    lines.push(
      data.conditionsState === DisplayRightsProposalStatus.DRAFT
        ? { key: 'mentions.boost.launch.gate.blocked.conditionsDraft' }
        : {
            key: 'mentions.boost.launch.gate.blocked.conditions',
            params: { state: data.conditionsState },
          }
    );
  }

  return lines;
}
